import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import CustomButton from '../../../components/common/CustomButton';

export function ProblemStatementSection() {
  const { palette, typography, breakpoints } = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(breakpoints.down('sm'));
  return (
    <Box
      id="problem-statement"
      component="section"
      sx={{
        bgcolor: palette.primary.main,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 3, md: 6 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: { xs: 3, md: 4 },
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: {
                xs: typography.h3.fontSize,
                md: typography.h1.fontSize,
              },
              fontWeight: {
                xs: typography.h3.fontWeight,
                md: typography.h1.fontWeight,
              },
              color: palette.primary.contrastText,
              textAlign: 'center',
              '& i': {
                color: palette.secondary.main,
              },
            }}
          >
            You Carry Your Family's History. <i>Who Will Carry It Next?</i>
          </Typography>
          <Typography
            sx={{
              color: palette.primary.contrastText,
              fontSize: {
                xs: typography.body1.fontSize,
                md: typography.h4.fontSize,
              },
              fontWeight: {
                xs: typography.h3.fontWeight,
              },
              py: 3,
              lineHeight: '1.8',
              textAlign: 'center',
            }}
          >
            Distance from the homeland, assimilation pressures, and time are
            quietly erasing African diaspora heritage. The dialect your
            grandmother spoke. The village your grandfather came from. The
            traditions that made your family unique.
          </Typography>
        </Box>
        <Container maxWidth="md">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#1f9954ff',
              borderRadius: 3,
              py: 2,
              px: 3,
              animation: 'slideUp 0.8s ease-out forwards',
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
              },
              '@keyframes slideUp': {
                from: {
                  opacity: 0,
                  transform: 'translateY(40px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
            component="section"
            role="region"
            aria-labelledby="problem-statement"
          >
            <Typography
              sx={{
                textAlign: 'center',
                fontStyle: 'italic',
                fontSize: {
                  xs: typography.body1.fontSize,
                  md: typography.h5.fontSize,
                },
                fontWeight: {
                  xs: typography.h3.fontWeight,
                  md: typography.h4.fontWeight,
                },
                color: palette.primary.contrastText,
                pt: { xs: 2, sm: 4 },
              }}
            >
              When heritage lives only in word of mouth, it disappears when our
              elders pass away.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                py: 2,
                textAlign: 'left',
              }}
            >
              <Box
                sx={{
                  borderLeft: '2px solid',
                  borderLeftColor: palette.secondary.main,
                  borderLeftWidth: 2,
                  paddingLeft: 2,
                }}
              >
                <Typography color={palette.primary.contrastText}>
                  Our heritage is all word of mouth... it's getting lost because
                  it is not written down
                </Typography>
                <Typography color={palette.secondary.main}>
                  — Focus group participant
                </Typography>
              </Box>
              <Box
                sx={{
                  borderLeft: '2px solid',
                  borderLeftColor: palette.secondary.main,
                  borderLeftWidth: 2,
                  paddingLeft: 2,
                }}
              >
                <Typography color={palette.primary.contrastText}>
                  I worry about my grandchildren losing connection to their
                  African roots
                </Typography>
                <Typography color={palette.secondary.main}>
                  — Grandparent, Malawian heritage
                </Typography>
              </Box>
              <Box
                sx={{
                  borderLeft: '2px solid',
                  borderLeftColor: palette.secondary.main,
                  borderLeftWidth: 2,
                  paddingLeft: 2,
                }}
              >
                <Typography color={palette.primary.contrastText}>
                  It takes so much effort to get the kids engaged with their
                  heritage
                </Typography>
                <Typography color={palette.secondary.main}>
                  — Parent of three
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              py: 2,
              mt: 3,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              component="h3"
              sx={{
                fontSize: {
                  xs: typography.h4.fontSize,
                  md: typography.h3.fontSize,
                },
                fontWeight: {
                  xs: typography.h4.fontWeight,
                  md: typography.h1.fontWeight,
                },
                color: palette.secondary.main,
              }}
            >
              LegacyTies transforms oral tradition into permanent legacy.
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: {
                  xs: typography.body1.fontSize,
                  md: typography.h4.fontSize,
                },
                fontWeight: {
                  xs: typography.body2.fontWeight,
                  md: typography.body1.fontWeight,
                },
                color: palette.primary.contrastText,
              }}
            >
              So your family's story survives for generations not yet born.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifySelf: 'center',
              alignItems: 'center',
            }}
          >
            <CustomButton
              label="Save Their Stories – Free"
              color="secondary"
              onClick={() => navigate('/auth/signup')}
              size={isSmallScreen ? 'small' : 'medium'}
              sx={{
                fontSize: {
                  xs: '0.8rem',
                  sm: '0.9rem',
                  md: typography.body1.fontSize,
                },
                color: palette.primary.main,
                width: { xs: '100%', sm: 'auto' },
                whiteSpace: 'nowrap',
                textAlign: 'center',
                '&:hover': {
                  color: 'white',
                },
              }}
            />
          </Box>
        </Container>
      </Container>
    </Box>
  );
}
